/**
 * Confirmation for "Pull from track record" — says, in plain words, what the
 * button is about to do (POST /experience/populate_from_extracts) before it
 * does it. Built on the form system (FORM_STYLE_GUIDE.md §12 shell).
 */
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CloudDownloadOutlined from "@mui/icons-material/CloudDownloadOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { FormFooter, FormNote } from "../../../components/forms/FormPrimitives";
import { FORM, formSx, px } from "../../../theme/formStyles";

const Line = ({ children }) => (
  <Box component="li" sx={{ ...formSx.body, color: FORM.color.ink2, mb: px(6) }}>
    {children}
  </Box>
);

Line.propTypes = { children: PropTypes.node };

const PullFromTrackRecordDialog = ({ open, onClose, onConfirm, busy, itemCount = 0 }) => (
  <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { ...formSx.paper, maxWidth: 560 } }}>
    <Box sx={formSx.head}>
      <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
        <Box sx={{ minWidth: 0 }}>
          <Txt sx={formSx.title}>Pull from track record</Txt>
          <Txt sx={formSx.titleHelper}>Add the properties from the borrower's track-record paperwork to this list.</Txt>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={busy} aria-label="Close" sx={{ mt: px(-4), mr: px(-8) }}>
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Flx>
    </Box>

    <DialogContent sx={formSx.shellBody}>
      <Txt sx={{ ...formSx.body, display: "block", mb: px(FORM.s.s4) }}>
        The loan's documents include the borrower's (and contractor's) list of past projects. This reads those lists and adds
        each property to the track record for you.
      </Txt>

      <Txt sx={formSx.groupLabel}>What happens</Txt>
      <Box component="ul" sx={{ m: 0, pl: px(20), mb: px(FORM.s.s4) }}>
        <Line>One row is added for each property found — with its address, dates and prices already filled in.</Line>
        <Line>Each row is matched to the person or company it most likely belongs to, and the source paperwork is attached as proof.</Line>
        <Line>Properties already on the list are skipped, so it's safe to run more than once.</Line>
      </Box>

      <FormNote>
        <strong>Nothing is verified or counted yet.</strong> Every row comes in as a candidate. You still review each one — check who it
        belongs to, check the proof, then verify it.
      </FormNote>
    </DialogContent>

    <FormFooter status={itemCount ? `${itemCount} item${itemCount === 1 ? "" : "s"} already on the list — duplicates will be skipped.` : "The list is empty — everything found will be added."}>
      <Button variant="text" color="gray" onClick={onClose} disabled={busy} sx={formSx.button}>
        Cancel
      </Button>
      <Button onClick={onConfirm} disabled={busy} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CloudDownloadOutlined sx={{ fontSize: 16 }} />} sx={formSx.button}>
        Pull from track record
      </Button>
    </FormFooter>
  </Dialog>
);

PullFromTrackRecordDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  busy: PropTypes.bool,
  itemCount: PropTypes.number,
};

export default PullFromTrackRecordDialog;
